import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { authenticateDemo, fetchGame } from '@/api/library';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Spinner } from '@/components/ui/Spinner';
import { StatusChip } from '@/components/ui/StatusChip';
import { useI18n } from '@/i18n';
import { displayTitle } from '@/lib/displayTitle';
import { gameDataPdfUrl } from '@/lib/gameDownloads';
import { gamePoster, gamePosterFallback, gameWideThumb } from '@/lib/gameArt';
import { buildRules, buildSpecFlags, buildSpecGroups, buildSpecs, currencyOptions, languageName, languageOptions, uiLanguageCodes, withLaunchLanguage } from '@/lib/gameSpecs';
import { liveCopy } from '@/lib/liveTables';
import { slotCopy } from '@/lib/slotCopy';
import type { DemoAuthResult, HubGame } from '@/types';

function PlatformIcons({ label }: { label: string }) {
  return (
    <span className="game-platforms" aria-label={label}>
      <svg className="game-platforms__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2.5" y="4" width="19" height="12.5" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 20.5h8M12 16.5v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
      <svg className="game-platforms__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5.5" y="2.5" width="13" height="19" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10 18.5h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
      <svg className="game-platforms__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="7.5" y="2.5" width="9" height="19" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M11 18.8h2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/** The editorial sheet marks paragraph lead-ins with **bold**. */
function CopyParagraph({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <p className={className}>
      {parts.map((part, index) =>
        index % 2 === 1 ? <strong key={`${part}-${index}`}>{part}</strong> : part,
      )}
    </p>
  );
}

function GameBackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="back-link">
      <svg className="back-link__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M10 3L5 8l5 5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{label}</span>
    </Link>
  );
}

function useDesktopDemo() {
  const [desktop, setDesktop] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const sync = () => setDesktop(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  return desktop;
}

export function GameDetailPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const desktopDemo = useDesktopDemo();

  const [game, setGame] = useState<HubGame | null>(null);
  const [poster, setPoster] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currency, setCurrency] = useState('EUR');
  const [language, setLanguage] = useState('en');
  const [session, setSession] = useState<DemoAuthResult | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const autoLaunchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!gameId || !accessToken) return;
    setLoading(true);
    setSession(null);
    setDemoError(null);
    // Allow the auto-launch below to run again, e.g. after an interface language change.
    autoLaunchedFor.current = null;
    fetchGame(gameId, accessToken, sessionId)
      .then((loaded) => {
        setGame(loaded);
        setPoster(gamePoster(loaded));
        if (loaded.details?.currency) setCurrency(loaded.details.currency);
        const languages = languageOptions(loaded, locale).map((option) => option.code);
        setLanguage(
          languages.includes(locale) ? locale : languages.includes('en') ? 'en' : languages[0] || 'en',
        );
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : t('game.notFound'));
      })
      .finally(() => setLoading(false));
  }, [gameId, accessToken, sessionId, locale, t]);

  const launchDemo = useCallback(async () => {
    if (!gameId || !accessToken) return;
    setDemoError(null);
    setDemoLoading(true);
    try {
      const result = await authenticateDemo(gameId, accessToken, sessionId, {
        currency,
        language,
      });
      if (!result.url) throw new ApiError(t('game.demoUrlMissing'), 502);
      setSession(result);
    } catch (err) {
      setDemoError(err instanceof ApiError ? err.message : t('game.demoFailed'));
      setSession(null);
    } finally {
      setDemoLoading(false);
    }
  }, [gameId, accessToken, sessionId, currency, language, t]);

  // Desktop opens the demo immediately; phones wait on a Play demo tap.
  useEffect(() => {
    if (!desktopDemo) return;
    if (!game || !game.flags?.demoCapable) return;
    if (autoLaunchedFor.current === game.id) return;
    autoLaunchedFor.current = game.id;
    void launchDemo();
  }, [game, launchDemo, desktopDemo]);

  const specs = useMemo(() => (game ? buildSpecs(game, t, locale) : []), [game, t, locale]);
  const specGroups = useMemo(() => (game ? buildSpecGroups(game, t, locale) : []), [game, t, locale]);
  const specFlags = useMemo(() => (game ? buildSpecFlags(game, t) : []), [game, t]);
  const rules = useMemo(() => (game ? buildRules(game) : []), [game]);
  const live = useMemo(() => (game ? liveCopy(game, locale) : null), [game, locale]);
  const slot = useMemo(() => (game ? slotCopy(game) : null), [game]);

  if (loading) {
    return (
      <div className="page-loading">
        <Spinner label={t('game.loading')} />
      </div>
    );
  }

  if (error || !game) {
    return (
      <section className="site-pad-x page-narrow">
        <ErrorMessage message={error ?? t('game.notFound')} />
        <GameBackLink to="/library" label={t('game.back')} />
      </section>
    );
  }

  const details = game.details ?? {};
  const config = details.config ?? {};
  const features = config.features ?? [];
  const wideThumb = gameWideThumb(game);
  const specsArt = wideThumb ?? poster ?? gamePosterFallback(game);
  const dataPdf = gameDataPdfUrl(game);

  const demoBlockedReason = game.flags?.isInMaintenance
    ? t('game.blockedMaintenance')
    : game.flags?.isComingSoon
      ? t('game.blockedComingSoon')
      : !game.flags?.demoCapable
        ? t('game.blockedNoDemo')
        : null;

  const frameSrc = session?.url ? withLaunchLanguage(session.url, language) : null;

  async function copyLaunchLink() {
    if (!frameSrc) return;
    await navigator.clipboard.writeText(frameSrc);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  const tableHeaders = [
    t('liveTable.gameId'),
    t('liveTable.tableName'),
    t('liveTable.minLimit'),
    t('liveTable.maxLimit'),
    t('liveTable.hours'),
    t('liveTable.language'),
    t('liveTable.releaseDate'),
    t('liveTable.location'),
  ];

  return (
    <section className="game-page">
      <div className="game-page__bar site-pad-x">
        <GameBackLink to={`/library?category=${game.category}`} label={t('game.back')} />
        <nav className="crumbs" aria-label={t('game.breadcrumbAria')}>
          <Link to={`/library?category=${game.category}`}>
            {game.category === 'live' ? t('game.liveCasino') : t('game.slots')}
          </Link>
          <span aria-hidden="true">/</span>
          <span className="crumbs__current">{displayTitle(game.title)}</span>
        </nav>
      </div>

      <div className="game-page__grid site-pad-x">
        <div className="game-page__main">
          <div className="game-page__stack">
          {/* Phones lead with the key art; the stage replaces it once a demo runs. */}
          <img
            className={wideThumb ? 'game-mobile-art game-mobile-art--wide' : 'game-mobile-art'}
            src={specsArt}
            alt={displayTitle(game.title)}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = gamePosterFallback(game);
              event.currentTarget.classList.remove('game-mobile-art--wide');
            }}
          />

          <div className={`game-stage${frameSrc ? ' is-open' : ''}`}>
            {frameSrc ? (
              <iframe
                key={frameSrc}
                className="game-stage__frame"
                src={frameSrc}
                title={t('game.demoFrameTitle', { title: displayTitle(game.title) })}
                allow="autoplay; fullscreen; clipboard-write"
                allowFullScreen
              />
            ) : (
              <div
                className="game-stage__poster"
                style={{ backgroundImage: `url(${poster ?? gamePosterFallback(game)})` }}
              >
                <div className="game-stage__veil" aria-hidden="true" />
                <div className="game-stage__cta">
                  {demoLoading ? (
                    <Spinner label={t('game.startingDemo')} />
                  ) : demoBlockedReason ? (
                    <p className="game-stage__note">{demoBlockedReason}</p>
                  ) : (
                    <Button className="game-stage__play" onClick={launchDemo}>
                      {t('upcoming.playDemo')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {session ? <span className="game-stage__badge">{t('game.demoBadge')}</span> : null}
          </div>

          {/* A title without a demo build still shows the button, disabled,
              so the artwork is always followed by the same call to action. */}
          {!desktopDemo && !frameSrc ? (
            <>
              <button
                type="button"
                className="game-play-bar"
                onClick={launchDemo}
                disabled={demoLoading || Boolean(demoBlockedReason)}
              >
                {demoLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M5 3.5v9l8-4.5-8-4.5Z" />
                  </svg>
                )}
                {demoLoading ? t('game.startingDemo') : t('upcoming.playDemo')}
              </button>
              {demoBlockedReason ? (
                <p className="game-stage__note game-play-bar__note">{demoBlockedReason}</p>
              ) : null}
            </>
          ) : null}

          <div className="game-page__body">
          <div className="game-toolbar">
            <label className="game-toolbar__field">
              <span>{t('game.currency')}</span>
              <select
                className="contact-input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {currencyOptions(game).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>

            <label className="game-toolbar__field">
              <span>{t('game.language')}</span>
              <select
                className="contact-input"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {languageOptions(game, locale).map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="game-toolbar__actions">
              <Button
                variant="glass"
                onClick={launchDemo}
                disabled={demoLoading || Boolean(demoBlockedReason)}
              >
                {session ? t('game.reloadDemo') : t('game.startDemo')}
              </Button>
              <Button variant="glass" disabled={!session?.url} onClick={copyLaunchLink}>
                {copied ? t('game.copied') : t('game.copyLink')}
              </Button>
            </div>
          </div>

          {demoError ? (
            <ErrorMessage message={demoError} title={t('game.demoFailedTitle')} />
          ) : null}

          <header className="game-headline">
            <div>
              <h1 className="game-headline__title display">{displayTitle(game.title)}</h1>
              {details.symbol ? (
                <p className="game-headline__symbol">{details.symbol}</p>
              ) : null}
            </div>
            <div className="game-headline__chips">
              {game.flags?.isInMaintenance ? <StatusChip status="maintenance" /> : null}
              {game.flags?.isComingSoon ? <StatusChip status="coming_soon" /> : null}
              {details.highlighted ? (
                <span className="chip chip--gold">{t('game.highlighted')}</span>
              ) : null}
              {details.provider ? <span className="chip">{details.provider}</span> : null}
              {details.gameType ? <span className="chip">{details.gameType}</span> : null}
            </div>
          </header>

          {live ? (
            <div className="game-copy">
              {live.intro.map((paragraph) => (
                <p key={paragraph} className="game-page__lead">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : slot ? (
            <div className="game-copy">
              <p className="game-page__lead">{slot.lead}</p>
              {slot.intro.map((paragraph) => (
                <CopyParagraph key={paragraph} text={paragraph} className="game-copy__text" />
              ))}
            </div>
          ) : game.description ? (
            <p className="game-page__lead">{game.description}</p>
          ) : null}

          {slot?.sections.map((section) => (
            <section key={section.heading} className="game-block">
              <h2 className="section-title">{section.heading}</h2>
              {section.paragraphs?.length ? (
                <div className="game-copy">
                  {section.paragraphs.map((paragraph) => (
                    <CopyParagraph key={paragraph} text={paragraph} className="game-copy__text" />
                  ))}
                </div>
              ) : null}
              {section.bullets?.length ? (
                <ul className="game-copy__list">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          {live ? (
            <section className="game-block">
              <h2 className="section-title">{t('game.keyFeatures')}</h2>
              <ul className="game-copy__list">
                {live.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {live ? (
            <section className="game-block">
              <h2 className="section-title">{t('liveTable.title')}</h2>
              <div className="live-tables__scroll">
                <table className="live-tables">
                  <thead>
                    <tr>
                      {tableHeaders.map((header) => (
                        <th key={header} scope="col">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {live.tables.map((row) => (
                      <tr key={`${row.gameId}-${row.tableName}`}>
                        <td>{row.gameId}</td>
                        <td>{row.tableName}</td>
                        <td>{row.minLimit}</td>
                        <td>{row.maxLimit}</td>
                        <td>{row.hours}</td>
                        <td>{row.language}</td>
                        <td>{row.releaseDate}</td>
                        <td>{row.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {features.length > 0 ? (
            <section className="game-block">
              <h2 className="section-title">{t('game.features')}</h2>
              <div className="feature-grid">
                {features.map((feature) => (
                  <span key={feature} className="feature-pill">
                    {feature.replace(/([a-z])([A-Z])/g, '$1 $2')}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {/* Slot math now reads from the sheet in the sidebar, so only live
              tables still need the raw config listing. */}
          {game.category === 'live' && rules.length > 0 ? (
            <section className="game-block">
              <h2 className="section-title">{t('game.tableRules')}</h2>
              <div className="rules-grid">
                {rules.map((rule) => (
                  <div key={rule.label} className="rules-grid__row">
                    <span className="rules-grid__label">{rule.label}</span>
                    <span className="rules-grid__value">{rule.value}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          </div>
          </div>
        </div>

        <aside className="game-specs">
          <div className="game-specs__card glass-panel-soft">
            <img
              className={wideThumb ? 'game-specs__art game-specs__art--wide' : 'game-specs__art'}
              src={specsArt}
              alt={displayTitle(game.title)}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = gamePosterFallback(game);
                event.currentTarget.classList.remove('game-specs__art--wide');
              }}
            />
            <h2 className="game-specs__heading">{t('game.information')}</h2>
            <dl className="spec-list">
              {specs.map((spec) => (
                <div key={spec.label} className="spec-list__row">
                  <dt>{spec.label}</dt>
                  <dd>{spec.value}</dd>
                </div>
              ))}
            </dl>

            {specGroups.length > 0 ? (
              <div className="spec-groups">
                {specGroups.map((group) => (
                  <div key={group.label} className="spec-group">
                    <p className="spec-group__label">{group.label}</p>
                    <ul className="spec-group__list">
                      {group.items.map((item, index) => (
                        <li key={`${item}-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}

            {details.keywords?.length ? (
              <div className="game-specs__tags">
                {details.keywords.map((keyword) => (
                  <span key={keyword} className="chip">
                    {keyword}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="game-specs__card game-specs__card--pad glass-panel-soft">
            <dl className="spec-list spec-list--flush">
              <div className="spec-list__row spec-list__row--center">
                <dt>{t('game.availableOn')}</dt>
                <dd>
                  <PlatformIcons
                    label={`${t('game.platformDesktop')}, ${t('game.platformTablet')}, ${t('game.platformMobile')}`}
                  />
                </dd>
              </div>
              {specFlags.map((flag) => (
                <div key={flag.label} className="spec-list__row">
                  <dt>{flag.label}</dt>
                  <dd>{flag.value}</dd>
                </div>
              ))}
            </dl>
            <div className="game-lang-block">
              <p className="game-lang-block__label">{t('game.operatingLanguage')}</p>
              <ul className="game-lang-list">
                <li>{languageName('en', locale)}</li>
              </ul>
            </div>
            <details className="game-lang-block">
              <summary className="game-lang-block__summary">
                <span>{t('game.uiLanguages')}</span>
                <svg className="game-lang-block__chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3.5 6.25 8 10.75l4.5-4.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </summary>
              <ul className="game-lang-list">
                {uiLanguageCodes(game).map((code) => (
                  <li key={code}>{languageName(code, locale)}</li>
                ))}
              </ul>
            </details>
          </div>

          <div className="game-specs__card game-specs__card--pad glass-panel-soft">
            <h2 className="game-specs__heading game-specs__heading--flush">{t('game.downloads')}</h2>
            <ul className="game-download-list">
              <li className="game-download-list__row">
                <span>{t('game.mediaPack')}</span>
                <Link
                  className="btn btn-primary game-download-btn"
                  to={`/assets?gameId=${encodeURIComponent(game.id)}`}
                >
                  {t('game.download')}
                </Link>
              </li>
              <li className="game-download-list__row">
                <span>{t('game.gameData')}</span>
                {dataPdf ? (
                  <a className="btn btn-primary game-download-btn" href={dataPdf} download>
                    {t('game.download')}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary game-download-btn"
                    disabled
                    title={t('game.gameDataSoon')}
                  >
                    {t('game.download')}
                  </button>
                )}
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}
